/*global swal*/
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useAxios from 'axios-hooks';
import { useNavigate, useParams } from 'react-router-dom';
import isEmpty from 'lodash.isempty';
import classNames from 'classnames';

import { EVISIT_STATUS } from '../../../config/constants';
import PatientBar from './patientbar';
import ChangeFeeModal from './change-fee';
import ReferralModal from './referral';
import EvisitTabs from './EvisitTabs';
import Chat from './chat';
import VideoChat from '../video-chat';

const MODEL_FILTER = JSON.stringify({
	include: [
		{
			relation: 'patient',
			scope: {
				include: [
					{
						relation: 'insurances',
						scope: {
							limit: 1,
							order: ['id DESC'],
						},
					},
				],
			},
		},
		'provider',
		'patient_dependent',
		'provider_visit_type',
		'care_quest_response_groups',
		{
			relation: 'evisit_provider_transactions',
			scope: {
				limit: 5,
				order: 'update_dttm DESC',
				include: 'provider',
			},
		},
		{ 'evisit-referral': { 'closedBy-provider': 'provider_practice' } },
	],
});

const Evisit = ({ showNotification, appSocket, user }) => {
	const [isPatientConnected, setPatientConnected] = useState(false);
	const [isChangeFeeModalOpen, setChangeFeeModalOpen] = useState(false);
	const [isReferralModalOpen, setReferralModalOpen] = useState(false);
	const [isShowingVideoChat, setShowingVideoChat] = useState(false);
	const [chatToken, setChatToken] = useState();
	const { id: evisitId } = useParams();
	const navigate = useNavigate();

	const [{ data: evisitModel, loading }, loadEvisit] = useAxios({
		method: 'GET',
		url: `/evisits/${evisitId}`,
		params: { filter: MODEL_FILTER },
	});
	const [{ data: result, loading: saving }, save] = useAxios(
		{
			method: 'PUT',
			url: `/evisits/${evisitId}`,
		},
		{ manual: true }
	);
	const [, cancelEvisit] = useAxios(
		{ method: 'POST', url: `/evisits/${evisitId}/cc-cancel-before-capture` },
		{ manual: true }
	);
	const [, addProviderTransaction] = useAxios(
		{ method: 'POST', url: '/evisit_provider_transactions' },
		{ manual: true }
	);
	const [, endEvisit] = useAxios(
		{ method: 'POST', url: `/evisits/${evisitId}/cc-end-evisit` },
		{ manual: true }
	);
	const [, changeFee] = useAxios(
		{ method: 'POST', url: `/evisits/${evisitId}/cc-charge-refund` },
		{ manual: true }
	);
	const [{ data: tokenData }, getProviderVideoToken] = useAxios({
		method: 'GET',
		url: `/evisits/${evisitId}/get-provider-video-token`,
	});
	const [, reinvitePatient] = useAxios(
		{
			method: 'POST',
			url: `/providers/${user.get('id')}/evisit-reinvite`,
			data: { evisitId },
		},
		{ manual: true }
	);

	const showTrackingBoard = () => {
		navigate('/trackingboard');
	};

	const model = { ...evisitModel, ...result };

	const closeEvisit = (options) => {
		endEvisit({ data: { ...options, provider_id: user.get('id') } }).then(
			() => {
				appSocket.emit('end evisit');
				swal(
					{
						title: 'Visit Ended',
						text: 'This visit has ended and has been closed. If you would like to review anything, click on Previous Visits on the upper left. You will now return to main menu.',
						type: 'success',
					},
					showTrackingBoard
				);
			}
		);
	};

	const changeStatus = (status) => {
		addProviderTransaction({
			data: {
				providerId: user.get('id'),
				evisitId: model.id,
				status,
				update_dttm: new Date(),
			},
		})
			.then(() => {
				saveEvisit({ evisit_status: status });
			})
			.catch(() => {
				showNotification({
					message: 'Unable to change visit status!',
					status: 'danger',
				});
			});
	};

	const startEvisit = () => {
		changeStatus(EVISIT_STATUS.IN_PROGRESS);
	};

	const onReferVisit = ({
		referralReason,
		practiceId,
		providerId,
		specialty,
	}) => {
		setReferralModalOpen(false);
		swal(
			{
				title: 'Are you sure?',
				text: 'You are about to refer the patient and end this visit.',
				type: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#DD6B55',
				confirmButtonText: 'Yes',
				closeOnConfirm: false,
				showLoaderOnConfirm: true,
			},
			() => {
				closeEvisit({
					referral_reason: referralReason,
					referring_to_provider_practice_id: practiceId,
					referring_to_provider_id: providerId,
					referral_specialty: specialty,
				});
			}
		);
	};

	const saveEvisit = (data = {}) => {
		// make user happy
		if (isEmpty(data)) {
			showNotification({
				status: 'success',
				message: 'Visit Saved',
				timeout: 1000,
			});
			return;
		}
		//  check for equality and conditionally save
		let needsSaving = false;
		Object.keys(data).forEach((key) => {
			if (!needsSaving && (!isEmpty(data[key]) || !isEmpty(model[key]))) {
				needsSaving = data[key] !== model[key];
			}
		});
		if (needsSaving) {
			save({ data })
				.then(() => {
					showNotification({
						status: 'success',
						message: 'Visit Saved',
						timeout: 1000,
					});
				})
				.catch(() => {
					showNotification({
						status: 'danger',
						message:
							'There was a problem saving this visit. Please try again. If problem persists, contact support.',
					});
				});
		}
	};

	const handleCancelEvisit = (reason) => {
		cancelEvisit({ data: { userId: user.get('id'), reason } }).then(() => {
			swal(
				{
					title: 'Visit Canceled',
					text: 'Returning you to the Tracking Board',
					closeOnConfirm: true,
					type: 'success',
				},
				showTrackingBoard
			);
		});
	};

	const evisitEnded = () => {
		loadEvisit().then(() => {
			swal(
				{
					title: 'Visit Ended by Patient',
					text: 'This visit has been ended by the patient and is now closed. If you would like to review anything, click on Previous Visits on the upper left. You will now return to main menu.',
					type: 'success',
				},
				showTrackingBoard
			);
		});
	};

	const handleReinvitePatient = (event) => {
		event.preventDefault();
		reinvitePatient()
			.then(() => {
				showNotification({
					message: 'Patient invite sent',
					status: 'success',
				});
			})
			.catch(() => {
				showNotification({
					message: 'Patient invite not sent!',
					status: 'danger',
				});
			});
	};

	const handleShowVideoChat = (chatData) => {
		appSocket.emit('video request', chatData);
		getProviderVideoToken()
			.then(() => {
				setChatToken(tokenData.token);
				setShowingVideoChat(true);
			})
			.catch(() => {
				showNotification({
					message: 'Unable to get provider video chat token!',
					status: 'danger',
				});
			});
	};

	if (loading && isEmpty(model)) {
		return <div className="content-wrapper evisit">Loading...</div>;
	}

	return (
		<>
			<section className={classNames({ hidden: isShowingVideoChat })}>
				<div className="main-content">
					<div className="content-wrapper evisit">
						<div id="evisit-chat" className="chat">
							<Chat
								user={user}
								appSocket={appSocket}
								onEvisitClosed={evisitEnded}
								evisit={model}
								interactive={!model?.end_dttm}
								onGoToVideoChat={handleShowVideoChat}
								isPatientConnected={isPatientConnected}
								setPatientConnected={(isPatientConnected) => {
									setPatientConnected(isPatientConnected);
								}}
								patient={model?.patient}
							/>
						</div>
						<div className="content">
							<div id="patient-bar" className="patient-bar clearfix">
								<PatientBar
									user={user}
									evisit={model}
									isPatientConnected={isPatientConnected}
									onReinvitePatient={handleReinvitePatient}
									onChangeFee={() => setChangeFeeModalOpen(true)}
									onCancelVisit={handleCancelEvisit}
									onStartVisit={startEvisit}
									onEndVisit={() => closeEvisit()}
									onReferVisit={() => setReferralModalOpen(true)}
									isDependentEvisit={
										model?.patientDependentId && model?.patient_dependent
									}
									isUpdating={loading || saving}
								/>
							</div>
							<div id="evisit-tabs" role="tabpanel" className="evisit-tabs">
								<EvisitTabs
									appSocket={appSocket}
									evisit={model}
									user={user.toJSON()}
									onShowNotification={showNotification}
									isDependentEvisit={
										model?.patientDependentId && model?.patient_dependent
									}
									assessment={
										Array.isArray(model?.care_quest_response_groups) &&
										model.care_quest_response_groups.length > 0
											? model.care_quest_response_groups[0]
											: null
									}
									onUpdateBillingCode={(billing_code_type_id) => {
										saveEvisit({ billing_code_type_id });
									}}
									hasBillingCodeFeature={
										user.get('provider_practice').enable_billing_code_type_ind
									}
									onSOAPFieldChange={(field, value) => {
										saveEvisit({ [field]: value });
									}}
									onSaveVisit={saveEvisit}
									isUpdating={loading || saving}
								/>
							</div>
						</div>
						<ChangeFeeModal
							isOpen={isChangeFeeModalOpen}
							onClose={() => setChangeFeeModalOpen(false)}
							paymentAmount={model?.paymentAmount}
							onSave={(change) => {
								setChangeFeeModalOpen(false);
								changeFee({ data: change })
									.then(({ data }) => {
										if (
											data?.cb.charge_refund &&
											data.cb.charge_refund === 'successful'
										) {
											showNotification({
												message: 'Fee updated successfully',
												status: 'success',
											});
											loadEvisit();
										} else {
											showNotification({
												message: 'Fee not changed - unknown error',
												status: 'warning',
											});
										}
									})
									.catch(({ response }) => {
										let message = 'Unable to change visit fee';
										if (response?.data?.error) {
											if (
												response.data.error.message.indexOf('refundReason') >= 0
											) {
												message = 'Fee not changed: Refund Reason is required.';
											}
										}
										showNotification({
											message,
											status: 'danger',
										});
									});
							}}
						/>
						<ReferralModal
							isOpen={isReferralModalOpen}
							onClose={() => setReferralModalOpen(false)}
							onReferVisit={onReferVisit}
							patientId={model?.patientId}
						/>
					</div>
				</div>
			</section>
			{isShowingVideoChat && (
				<div className="video-chat-wrapper">
					<VideoChat
						room={model.chatRoom}
						token={chatToken}
						appSocket={appSocket}
						onClose={() => setShowingVideoChat(false)}
					/>
				</div>
			)}
		</>
	);
};

Evisit.propTypes = {
	showNotification: PropTypes.func.isRequired,
	appSocket: PropTypes.object.isRequired,
	user: PropTypes.object.isRequired,
};

export default Evisit;
